import React, { useState, useCallback } from 'react';
import { 
  Paper, 
  Stack, 
  Title, 
  Text, 
  TextInput, 
  Button, 
  Alert,
  Group,
  Collapse,
} from '@mantine/core';
import { 
  IconTicket, 
  IconCheck, 
  IconAlertCircle, 
} from '@tabler/icons-react';
import { validateVoucher, redeemVoucher } from '../../services/voucherService';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

type VoucherRedemptionProps = {
  onSuccess: () => void;
  disabled?: boolean;
};

export const VoucherRedemption: React.FC<VoucherRedemptionProps> = ({ 
  onSuccess, 
  disabled = false 
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid?: boolean;
    reason?: string;
  } | null>(null);

  const handleValidateCode = useCallback(async () => {
    if (!code.trim() || !user) {
      setValidationResult(null);
      return;
    }

    setIsValidating(true);
    try {
      const token = await user.getIdToken();
      const result = await validateVoucher(code.trim(), token);
      setValidationResult(result);
    } catch (error) {
      console.error("Error validating voucher:", error);
      notifications.show({
        title: "Error",
        message: "Failed to validate voucher code",
        color: "red",
      });
    } finally {
      setIsValidating(false);
    }
  }, [code, user, setValidationResult, setIsValidating]); 

  const handleRedeemVoucher = async () => {
    if (!code.trim() || validationResult?.valid !== true || !user) return;

    setIsRedeeming(true);
    try {
      const token = await user.getIdToken();
      const result = await redeemVoucher(code.trim(), token);
      
      if (result.success) {
        onSuccess();
        navigate('/subscription/success');
      } else {
        notifications.show({
          title: 'Redemption failed',
          message: result.error || 'Failed to redeem voucher',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
      }
    } catch (error) {
      console.error('Error redeeming voucher:', error);
      notifications.show({
        title: 'Error',
        message: 'An unexpected error occurred while redeeming the voucher',
        color: 'red',
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  // Validate code whenever it changes (with debounce effect)
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleValidateCode();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [code, handleValidateCode]);

  return (
    <Paper 
      withBorder 
      p="xl" 
      radius="lg"
      style={{
        backgroundColor: 'white',
        borderColor: 'var(--mantine-color-gray-3)'
      }}
    >
      <Stack gap="xl" style={{ position: 'relative', zIndex: 2 }}>
        <Stack gap="xs" align="center" style={{ minHeight: '70px' }}>
          <Group gap="xs" align="center" wrap="nowrap">
            <IconTicket size={20} style={{ color: 'var(--mantine-color-gray-6)', flexShrink: 0 }} />
            <Title order={3} c="dark" ta="center">
              Have a voucher?
            </Title>
          </Group>
        </Stack>
        
        <Text size="xs" c="dimmed">
          Enter your subscription code
        </Text>

        <TextInput
          placeholder="Enter code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={disabled || isValidating || isRedeeming}
          size="sm"
          styles={{
            input: {
              fontFamily: 'monospace',
              letterSpacing: '0.05em'
            }
          }}
        />

        <Collapse in={!!validationResult}>
          <Alert
            icon={validationResult?.valid ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
            color={validationResult?.valid ? 'green' : 'red'}
            radius="lg"
          >
            {validationResult?.valid
              ? 'Valid code!'
              : validationResult?.reason || 'Invalid code'
            }
          </Alert>
        </Collapse>

        <Button
          onClick={handleRedeemVoucher}
          disabled={disabled || !code.trim() || validationResult?.valid !== true}
          loading={isRedeeming}
          variant="light"
          size="sm"
          leftSection={<IconCheck size={14} />}
          fullWidth
        >
          {isRedeeming ? 'Redeeming...' : 'Apply code'}
        </Button>

        {isValidating && (
          <Text size="xs" c="dimmed" ta="center">
            Checking code...
          </Text>
        )}
      </Stack>
    </Paper>
  );
};
